import type { ReactNode } from "react";
import {
  inspectSafetySchema, safetyEnumOptions, safetyLabel, safetyOptionPresentation, safetyRecord,
  type SafetySchema,
} from "../safetySchema";

export interface SafetyValueSummaryProps {
  value: unknown;
  schema?: SafetySchema;
  name?: string;
}

/** Read-only presentation of the supplied evidence; never seeds or changes an answer. */
export function SafetyValueSummary({ value, schema = {}, name = "" }: SafetyValueSummaryProps) {
  return <div className="safety-value-summary">{summarize(value, schema, schema, name || "safety-value", 0)}</div>;
}

function summarize(value: unknown, original: SafetySchema, root: SafetySchema, path: string, depth: number): ReactNode {
  if (value === undefined || value === null) return <span className="safety-value-summary__empty">Keine Angabe</span>;
  if (depth > 24) return <span className="safety-value-summary__empty">Weitere verschachtelte Angaben vorhanden.</span>;
  const schema = inspectSafetySchema(original, root).schema;
  if (Array.isArray(value)) {
    if (!value.length) return <span className="safety-value-summary__empty">Keine Angabe</span>;
    const itemSchema = safetyRecord(schema.items) ? schema.items : {};
    return <ol className="safety-value-summary__list">{value.map((entry, index) => <li className="safety-value-summary__entry" key={index}>{summarize(entry, itemSchema, root, `${path}[${index}]`, depth + 1)}</li>)}</ol>;
  }
  if (safetyRecord(value)) {
    const entries = Object.entries(value);
    if (!entries.length) return <span className="safety-value-summary__empty">Keine Angabe</span>;
    const properties = safetyRecord(schema.properties) ? schema.properties : {};
    return <dl className="safety-value-summary__object">{entries.map(([key, entry]) => {
      const childSchema = safetyRecord(properties[key]) ? properties[key] : {};
      const resolved = inspectSafetySchema(childSchema, root).schema;
      return <div className="safety-value-summary__row" key={key}>
        <dt>{safetyLabel(key, resolved, `${path}.${key}`)}</dt>
        <dd>{summarize(entry, childSchema, root, `${path}.${key}`, depth + 1)}</dd>
      </div>;
    })}</dl>;
  }
  if (typeof value === "boolean" || typeof value === "number" || typeof value === "string") {
    const option = safetyEnumOptions(schema).find(choice => Object.is(choice.value, value));
    const label = option?.label.trim() ? option.label : typeof value === "boolean" ? value ? "Ja" : "Nein" : String(value);
    const presentation = safetyOptionPresentation(value, label);
    return <span className="safety-value-summary__scalar" data-value-type={typeof value}>
      {presentation.graphic && <img className="safety-value-summary__graphic" src={presentation.graphic} alt={presentation.code?.startsWith("GHS") ? `${presentation.code}: ${presentation.label}` : `Symbol zum Hinweis ${presentation.code ?? ""}`} width={42} height={42}/>}
      <span className="safety-value-summary__copy">{presentation.code && <b className="safety-value-summary__code">{presentation.code}</b>}{presentation.label && <span>{presentation.label}</span>}{!presentation.code && !presentation.label && <span className="safety-value-summary__empty">Leerer Text</span>}</span>
    </span>;
  }
  return <span className="safety-value-summary__scalar">{String(value)}</span>;
}
